/**
 * @file messages.ts
 *
 * @fileoverview Keeps track of which items are added to the clean url part of a search page.
 * e.g. The url "/jobs/full-time/engineering" in this case the "cleanUrlitems" would have 2 things insie :
 * ['field_job_employment_type', 'field_job_occupational_field_parents_all']
 */

import { getCookie } from '../lib/session';

export interface URLfilter {
  key: string;
  value: string;
  alias?: string;
}

let urlItems = '{}';
if (process.browser) {
  const cookieValue = getCookie('cleanUrlitems', {});
  if (cookieValue) {
    urlItems = cookieValue;
  }
}

let cleanUrlitems: URLfilter[] = !process.browser
  ? []
  : Object.values(JSON.parse(urlItems)).map((e: any) => {
      return JSON.parse(e);
    });

/**
 * Inserts an item to the url map of clean url.
 *
 * @param {string} Item
 *   The item to add.
 */
export const addSearchUrlItem = (item: URLfilter) => {
  // Make sure we don't duplicate items only based on value.
  cleanUrlitems = cleanUrlitems.some((arrayItem) => {
    return (
      arrayItem.key === item.key &&
      arrayItem.value === item.value &&
      arrayItem.alias === item.alias
    );
  })
    ? cleanUrlitems
    : [...cleanUrlitems, item];
  return cleanUrlitems;
};

/**
 * Removes an item to the url map of clean url.
 *
 * @param {string} Item
 *   The item to remove.
 */
export const removeSearchUrlItem = (key: string) => {
  cleanUrlitems = cleanUrlitems.filter((i) => i.key !== key);
  return cleanUrlitems;
};

/**
 * Removes a value from the list of items that are currently in the url.
 *
 * @param {string} Item
 *   The item to remove.
 * @param {string} value
 *   The value of the item to remove.
 */
export const removeSearchUrlItemValue = (key: string, value: string) => {
  cleanUrlitems = cleanUrlitems.filter((i) => {
    return i.key !== key || i.value !== value;
  });
  return cleanUrlitems;
};

export const clearSearchUrlItems = () => (cleanUrlitems = []);

/**
 * Returns the search url items with only one item for each key.
 *
 * @returns {URLfilter[]}
 *  The search url items with only one item for each key.
 */
export const getSearchUrlItems = (): URLfilter[] => {
  const groupedByKey = cleanUrlitems.reduce((acc, item) => {
    if (!acc[item.key]) {
      acc[item.key] = [];
    }
    acc[item.key].push(item);
    return acc;
  }, {} as Record<string, URLfilter[]>);

  return Object.values(groupedByKey).map((group) => group[0]);
};
